<template lang="pug">
el-dialog(
  class="text-left-aligned"
  title="Alterar plano assinatura"
  :visible.sync="isVisible",
  width="720px"
)

  el-form(ref="form", :rules="rules", :model="data", label-width="200px")

    el-form-item(label="Novo plano de assinatura", prop="planId")
      el-select.stretched(v-model="data.planId")
        el-option-group(key="current", label="Planos atuais")
          el-option(
            v-for="plan in availablePlans"
            :label="`${plan.name}${plan.planId == subscription.planId ? ' (plano atual)' : ''}`"
            :value="plan.planId"
          )
            span(style='float: left;') {{plan.name}}
            span(v-if="plan.planId == subscription.planId", style='font-weight: bold; float: right;') (plano atual)


        el-option-group(key="old", label="Planos antigos")
          el-option(
            v-for="plan in oldMigrationPlans"
            :label="`${plan.name}${plan.planId == subscription.planId ? ' (plano atual)' : ''}`"
            :value="plan.planId"
          )
            span(style='float: left;') {{plan.name}}
            span(v-if="plan.planId == subscription.planId", style='font-weight: bold; float: right;') (plano atual)

    div(v-show="showAdvanced")
      el-form-item(label="A partir de", prop="startAt")
        el-date-picker.stretched(
          type="date",
          v-model="data.startAt",
          format="dd/MM/yyyy",
          value-format="yyyy-MM-ddT12:00:00.000Z"
          :clearable="false"
        )

      el-form-item(label="Interromper assinatura atual", prop="cancelCurrentSubscriptionImmediately")
        el-radio-group.stretched(v-model="data.cancelCurrentSubscriptionImmediately", size="small")
          el-radio-button(:label="false") Não
          el-radio-button(:label="true") Sim

  div(style="text-align: right;")
    el-button(v-if="showAdvanced", type="text", size="mini", @click="toggleAdvanced") Ocultar opções avançadas
    el-button(v-else, type="text", size="mini", @click="toggleAdvanced") Opções avançadas

  p
    | Ao alterar o plano de assinatura, esta assinatura&#32;
    span(v-if="data.cancelCurrentSubscriptionImmediately")
      | será
      b &#32;interrompida,&#32;
    | ficará com o status de 'Migrada' e
    b &#32;uma nova assinatura será iniciada&#32;
      span(v-if="fromSystemDate(data.startAt) == fromSystemDate(cancellationInfo.nextRenewalAt)") na próxima renovação ({{ fromSystemDate(data.startAt) }}).
      span(v-else) no dia {{ fromSystemDate(data.startAt) }}.

  p(v-if="showAdvanced && data.cancelCurrentSubscriptionImmediately")
    b Aviso:&#32;
    | Antes de alterar o plano de assinatura, certifique-se de que o início da nova assinatura&#32;
    | ocorrerá após o fim dos períodos pagos pela assinatura atual.

  .footer(slot="footer")
    el-button(@click="close", :disabled="isMigrating") Voltar

    el-button(type="primary", @click="promptMigration", :loading="isMigrating") Alterar plano

</template>
<script setup>
import { default as SubscriptionService, availablePlans, oldMigrationPlans } from '@/services/billing/SubscriptionService';
import { useMessage } from '@/utils/Message';
import { useConfirm } from '@/utils/MessageBox';
import { fromSystemDate } from '@/utils/DateTimeUtils';

import { nextTick, ref, watch } from 'vue';

const confirm = useConfirm()
const message = useMessage()

const props = defineProps({
  subscription: {
    type: Object,
    required: true
  },
  cancellationInfo: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['migrated'])

const form = ref()
const rules = ref({
  planId: [ { required: true, trigger: 'change', message: "Campo obrigatório" } ],
  startAt: [
    { required: true, trigger: 'change', message: "Campo obrigatório" },
    {
      validator: (rule, value, callback) => {
        const now = new Date()
        const date = new Date(value)
        const dayMillis = (1000 * 60 * 60 * 24)
        const startAtDay = Math.floor(date.getTime() / dayMillis)
        const today = Math.floor(now.getTime() / dayMillis)
        const nextRenewalDay = props.cancellationInfo?.nextRenewalAt ? Math.floor(new Date(props.cancellationInfo.nextRenewalAt).getTime() / dayMillis) : null

        if (startAtDay < today) {
          callback(new Error("Deve ser uma data futura"))
          return
        }

        if (nextRenewalDay && !data.value.cancelCurrentSubscriptionImmediately && startAtDay < nextRenewalDay) {
          callback(new Error("Deve iniciar após a data de renovação, caso não interrompa a assinatura atual"))
          return
        }

        callback()
      },
      trigger: 'change'
    }
  ],
  cancelCurrentSubscriptionImmediately: [ { required: true, trigger: 'change', message: "Campo obrigatório" } ],
})

const isVisible = ref(false)
const isMigrating = ref(false)
const showAdvanced = ref(false)

const data = ref({})

watch(() => data.value.cancelCurrentSubscriptionImmediately, () => {
  form.value?.validateField('startAt')
})

const toggleAdvanced = () => {
  showAdvanced.value = !showAdvanced.value

  if (!showAdvanced.value) {
    data.value.startAt = props.cancellationInfo.nextRenewalAt ? new Date(`${props.cancellationInfo.nextRenewalAt}T12:00:00.000Z`) : new Date()
    data.value.cancelCurrentSubscriptionImmediately = false
  }
}

const open = () => {
  const planId = availablePlans.find((plan) => plan.planId == props.subscription.planId)?.planId ??
    oldMigrationPlans.find((plan) => plan.planId == props.subscription.planId)?.planId ?? null

  const startAt =
    props.cancellationInfo.nextRenewalAt ? new Date(`${props.cancellationInfo.nextRenewalAt}T12:00:00.000Z`) : new Date()

  data.value = {
    planId: planId,
    startAt: startAt,
    cancelCurrentSubscriptionImmediately: false
  }

  isVisible.value = true
  showAdvanced.value = false

  nextTick(() => {
    form.value.clearValidate()
  })
}

const close = () => {
  isVisible.value = false
}

const promptMigration = async () => {
  var isValid = false

  try {
    await form.value.validate()

    isValid = true
  } catch(e) {
    message.warning("Verifique os dados preenchidos")

    return
  }

  confirm("Você tem certeza de que deseja migrar esta assinatura? Esta operação não pode ser desfeita", "Migrar assinatura", {
    confirmButtonText: 'Sim, migrar assinatura',
    cancelButtonText: 'Cancelar',
    type: 'warning'
  }).then(() => {
    return migrate()
  })
}

const migrate = async () => {
  isMigrating.value = true

  try {
    const newSubscription = await SubscriptionService.migrate(props.subscription.id, {
      planId: data.value.planId,
      startAt: data.value.startAt,
      bypassPlanSupportCheck: true,
      cancelCurrentSubscriptionImmediately: data.value.cancelCurrentSubscriptionImmediately ?? false
    })

    emit('migrated', newSubscription)

    message.success(`Assinatura migrada com sucesso!`)

    return newSubscription
  } catch(e) {
    message.error(`Houve um problema ao migrar assinatura: ${e}`)

    console.error(e)
  } finally {
    isMigrating.value = false

    close()
  }
}

defineExpose({
  open
})
</script>
<style lang="sass" scoped>
p
  word-break: break-word

.el-select
  width: 320px
</style>
